import { useEffect, useState, type ReactElement } from 'react'
import {
  listFamilyFees,
  getFamilyFeeReceiptPdfUrl,
  type ListFamilyFeesResult,
  type FamilyFeeDueItem,
} from '../../services/eduFeeService'
import { FamilyCard, Button, StatusChip, MiniLoader } from '../Common'
import { mapEduServerError } from '../Education/classesBuilderHelpers'
import { useNls } from '../../nls/nlsProvider'
import { formatMoney } from '../../utils/formatMoney'
import './familyFees.css'

export interface FamilyFeesPanelProps {
  workspaceId: string
  learnerId: string
  frozen?: boolean
  onBack?: () => void
}

export const FamilyFeesPanel = ({
  workspaceId,
  learnerId,
  frozen = false,
  onBack,
}: FamilyFeesPanelProps): ReactElement => {
  const { t } = useNls()
  const [activeTab, setActiveTab] = useState<'upcoming' | 'overdue' | 'paid'>('upcoming')
  const [data, setData] = useState<ListFamilyFeesResult | null>(null)
  const [loading, setLoading] = useState<boolean>(!frozen)
  const [error, setError] = useState<string | null>(null)
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)

  useEffect(() => {
    if (frozen) {
      return
    }
    let active = true
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setError(null)

    listFamilyFees({ workspaceId, learnerId })
      .then((res) => {
        if (!active) return
        setData(res)
      })
      .catch((err: unknown) => {
        if (!active) return
        setError(mapEduServerError(err, t))
      })
      .finally(() => {
        if (active) {
          setLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, frozen, t])

  if (frozen) {
    return (
      <div className="familyFeesPanel">
        {onBack && (
          <div className="familyFeesPanel__nav">
            <Button variant="tertiary" size="sm" onClick={onBack}>
              {t('NLSFamilyLeaveBack')}
            </Button>
          </div>
        )}
        <FamilyCard title={t('NLSFamilyNoticeHeading')}>
          <div className="familyFeesPanel__frozen">{t('NLSFamilyFrozenNotice')}</div>
        </FamilyCard>
      </div>
    )
  }

  const currency = data?.currency || null

  const getDuesList = (): FamilyFeeDueItem[] => {
    if (!data?.dues) return []
    if (activeTab === 'upcoming') return data.dues.upcoming || []
    if (activeTab === 'overdue') return data.dues.overdue || []
    return data.dues.paid || []
  }

  const currentDues = getDuesList()

  const handleOpenPdf = async (paymentId: string) => {
    setPdfLoadingId(paymentId)
    setPdfError(null)
    try {
      const res = await getFamilyFeeReceiptPdfUrl({ workspaceId, paymentId })
      if (res?.signedUrl) {
        window.open(res.signedUrl, '_blank')
      }
    } catch (err: unknown) {
      setPdfError(mapEduServerError(err, t))
    } finally {
      setPdfLoadingId(null)
    }
  }

  return (
    <div className="familyFeesPanel">
      {onBack && (
        <div className="familyFeesPanel__nav">
          <Button variant="tertiary" size="sm" onClick={onBack}>
            {t('NLSFamilyLeaveBack')}
          </Button>
        </div>
      )}

      {loading && !data && (
        <FamilyCard title={t('NLSFamilyFeesHeading')}>
          <div className="familyFeesPanel__loader">
            <MiniLoader />
          </div>
        </FamilyCard>
      )}

      {error && (
        <FamilyCard title={t('NLSFamilyFeesHeading')}>
          <div className="familyFeesPanel__notice">{error}</div>
        </FamilyCard>
      )}

      {data && (
        <>
          {!currency && (
            <div className="familyFeesPanel__notice">
              {t('NLSFamilyFeesCurrencyNotice')}
            </div>
          )}

          {/* Pay online card */}
          <FamilyCard title={t('NLSFamilyFeesHeading')}>
            <div className="familyFeesPanel__payOnlineSection">
              <Button variant="primary" disabled>
                {t('NLSFamilyFeesPayOnline')}
              </Button>
              <span className="familyFeesPanel__comingSoonNote">
                {t('NLSFamilyFeesPayOnlineComingSoon')}
              </span>
            </div>
          </FamilyCard>

          {/* Dues Card */}
          <FamilyCard title={t('NLSFamilyFeesHeading')}>
            <div className="familyFeesPanel__tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'upcoming'}
                className={`familyFeesPanel__tabBtn ${activeTab === 'upcoming' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('upcoming')}
              >
                {t('NLSFamilyFeesTabUpcoming')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'overdue'}
                className={`familyFeesPanel__tabBtn ${activeTab === 'overdue' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('overdue')}
              >
                {t('NLSFamilyFeesTabOverdue')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'paid'}
                className={`familyFeesPanel__tabBtn ${activeTab === 'paid' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('paid')}
              >
                {t('NLSFamilyFeesTabPaid')}
              </button>
            </div>

            {currentDues.length === 0 ? (
              <div className="familyFeesPanel__empty">{t('NLSFamilyFeesEmptyDues')}</div>
            ) : (
              <div className="familyFeesPanel__list">
                {currentDues.map((due) => (
                  <div key={due.id} className="familyFeesPanel__row">
                    <div className="familyFeesPanel__rowLeft">
                      <span className="familyFeesPanel__rowTitle">{due.planName || due.id}</span>
                      <span className="familyFeesPanel__rowSubtitle">{due.dueDate}</span>
                      {activeTab === 'overdue' && due.lateFeeMinor > 0 && currency && (
                        <span className="familyFeesPanel__lateFeeBadge">
                          {t('NLSFamilyFeesLateFeeBadge', {
                            amount: formatMoney(due.lateFeeMinor, currency),
                          })}
                        </span>
                      )}
                    </div>
                    <div className="familyFeesPanel__rowRight">
                      <span className="familyFeesPanel__rowAmount">
                        {currency
                          ? formatMoney(
                              activeTab === 'paid' ? due.paidMinor : due.balanceMinor,
                              currency,
                            )
                          : '—'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </FamilyCard>

          {/* Receipts Card */}
          <FamilyCard title={t('NLSFamilyFeesReceiptsHeading')}>
            {pdfError && <div className="familyFeesPanel__notice">{pdfError}</div>}
            {(!data.payments || data.payments.length === 0) ? (
              <div className="familyFeesPanel__empty">{t('NLSFamilyFeesEmptyReceipts')}</div>
            ) : (
              <div className="familyFeesPanel__list">
                {data.payments.map((p) => {
                  const isVoid = p.status === 'VOIDED'
                  return (
                    <div key={p.id} className="familyFeesPanel__row">
                      <div className="familyFeesPanel__rowLeft">
                        <div className="familyFeesPanel__receiptHead">
                          <span className="familyFeesPanel__rowTitle">#{p.receiptNo}</span>
                          {isVoid && (
                            <StatusChip tone="danger">
                              {t('NLSEducationFeesReceiptsVoidBadge')}
                            </StatusChip>
                          )}
                        </div>
                        <span className="familyFeesPanel__rowSubtitle">
                          {p.paidOn} · {p.method}
                        </span>
                        {p.reference && (
                          <span className="familyFeesPanel__rowSubtitle">{p.reference}</span>
                        )}
                        {isVoid && p.voidReason && (
                          <span className="familyFeesPanel__voidReason">{p.voidReason}</span>
                        )}
                      </div>
                      <div className="familyFeesPanel__rowRight">
                        <span className="familyFeesPanel__rowAmount">
                          {currency ? formatMoney(p.totalMinor, currency) : '—'}
                        </span>
                        <Button
                          size="sm"
                          variant="tertiary"
                          disabled={pdfLoadingId === p.id}
                          onClick={() => handleOpenPdf(p.id)}
                        >
                          {t('NLSEducationFeesPaymentOpenPdf')}
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </FamilyCard>
        </>
      )}
    </div>
  )
}
